import { useOutletContext } from "react-router-dom";
import { RiderModel, RiderProductModel } from "../../Api/Models/rider.model";
import { riderService } from "../../Api/Services/RiderService";
import { useLoading } from "../../Context/LoadingProvider";
import React, { useEffect, useState } from "react";
import { RiderProfileLayoutContextInterface } from "../../Pages/Rider/RiderProfileLayout";
import ImageInput from "../ImageInput";
import { t } from "i18next";
import { useInfoModal } from "../../Context/InfoModalProvider";
import { stockService } from "../../Api/Services/StockService";
import { StockModel } from "../../Api/Models/stock.model";
import ExchangeTshirts from "./ExchangeTshirts";
import {
  KitModel,
  KitProductModel,
  ProductModel,
} from "../../Api/Models/product.model";
import ExchangeMissing from "./ExchangeMissing";
import ProductState from "./ProductState";
import { ReturnCondition } from "../../Api/Models/return.model";
import { ExchangeLifecycleIcon } from "../Icons/ExchangeLifecycleIcon";
import { ExchangeAccidentIcon } from "../Icons/ExchangeAccidentIcon";
import { ExchangeExceptionIcon } from "../Icons/ExchangeExceptionIcon";
import { ExchangeSizeIcon } from "../Icons/ExchangeSizeIcon";
import { CheckIcon } from "../Icons/CheckIcon";
import { LongArrowLeftIcon } from "../Icons/LongArrowLeftIcon";
import { v4 as uuidv4 } from 'uuid';
import Popover from "../Popover";

interface ExchangeProps {
  gears: RiderProductModel[];
  rider: RiderModel;
  updateGears: CallableFunction;
  missingKitProducts: KitModel | null;
  fetchMissingKitProducts: CallableFunction;
}

export interface ExceptionItem {
  gearId: number;
  variationId: number | null;
  replacementProductId?: number | null;
  condition: ReturnCondition;
  image: File;
  note: string | null;
}

export interface MultiExchangeItem {
  gearId: number;
  variationId: number | null;
  groupSelectedProductId?: null | number;
  groupSelectedProductVariationId?: null | number;
  replacementProductId?: number | null;
  boxKey?: string | null;
}

export interface TshirtItem {
  productId: number;
  variationId: number;
  quantity: number;
}

const Exchange: React.FunctionComponent<ExchangeProps> = ({
                                                            gears,
                                                            rider,
                                                            updateGears,
                                                            missingKitProducts,
                                                            fetchMissingKitProducts,
                                                          }) => {
  const { startLoading, finnishLoading } = useLoading();
  const [images, setImages] = useState<File[]>([]);
  const [returnedItemsImages, setReturnedItemsImages] = useState<File[]>([]);
  const { showInfo } = useInfoModal();
  const [stocks, setStocks] = useState<StockModel[]>([]);
  const { setSubTitle, subTitle } =
      useOutletContext<RiderProfileLayoutContextInterface>();
  const [maxTshirtsCount, setMaxTshirtsCount] = useState<{
    maxTshirts: number;
    returnModifier: number;
  }>({ maxTshirts: 0, returnModifier: 0 });
  const [selectedGears, setSelectedGears] = useState<MultiExchangeItem[]>([]);
  const [returnedTshirts, setReturnedTshirts] = useState<TshirtItem[]>([]);
  const [dispatchedTshirts, setDispatchedTshirts] = useState<TshirtItem[]>([]);
  const [missingProducts, setMissingProducts] = useState<KitProductModel[]>(
      missingKitProducts?.products.filter((item: KitProductModel) => {
        return !gears.some((gear) => {
          return gear.replacementProduct?.some(
              (product) => product.id === item.productId
          );
        });
      }) ?? []
  );
  const [selectedExceptionGearId, setSelectedExceptionGearId] = useState<number | null>(null);
  const [tShirts, setTShirts] = useState<ProductModel[]>([]);
  const [exceptions, setExceptions] = useState<ExceptionItem[]>([]);
  const [boxKeys, setBoxKeys] = useState<{ [gearId: number]: string }>({});
  const [boxKeyErrors, setBoxKeyErrors] = useState<{ [gearId: number]: string | null }>({});
  const [tempBoxKey, setTempBoxKey] = useState<string>("");
  const [showBoxKeyModal, setShowBoxKeyModal] = useState(false);
  const [currentBoxGearId, setCurrentBoxGearId] = useState<number | null>(null);
  const [missingBoxKeys, setMissingBoxKeys] = useState<{ [kitProductId: number]: string }>({});
  const [showMissingBoxKeyModal, setShowMissingBoxKeyModal] = useState(false);
  const [currentMissingKitProductId, setCurrentMissingKitProductId] = useState<number | null>(null);
  const [tempMissingBoxKey, setTempMissingBoxKey] = useState("");
  const [missingBoxKeyErrors, setMissingBoxKeyErrors] = useState<{ [kitProductId: number]: string | null }>({});

  const getAllSelectedCountByCategory = (): Map<number, number> => {
    const categoryCount = new Map<number, number>();

    gears.forEach(gear => {
      if (gear.categoryId) {
        const currentCount = categoryCount.get(gear.categoryId) || 0;
        categoryCount.set(gear.categoryId, currentCount + 1);
      }
    });

    missingProducts
        .filter(p => p.checked && p.categoryId)
        .forEach(product => {
          const currentCount = categoryCount.get(product.categoryId!) || 0;
          categoryCount.set(product.categoryId!, currentCount + Math.max(product.quantity, 1));
        });

    exceptions.forEach(ex => {
      const gear = gears.find(g => g.id === ex.gearId);
      if (gear?.categoryId) {
        const currentCount = categoryCount.get(gear.categoryId) || 0;
        categoryCount.set(gear.categoryId, currentCount + 1);
      }
    });

    return categoryCount;
  };

  const getExchangeCategoryCount = (categoryId: number): number => {
    const notExpiredCount = gears.filter(
        g => g.categoryId === categoryId && (g.exchangeDays ?? 0) > 0
    ).length;

    let selectedCount = 0;
    selectedGears.forEach(sg => {
      const g = gears.find(gg => gg.id === sg.gearId);
      if (g?.categoryId === categoryId) selectedCount++;
    });

    return notExpiredCount + selectedCount;
  };

  const wouldExceedExchangeCategoryLimit = (gear: RiderProductModel): boolean => {
    if (gear.exchangeList === "size_change") return false;
    if (gear.exchangeList === "accident") return false;
    if (!gear.categoryId || !gear.categoryMaxProducts) return false;
    if (selectedGears.some(sg => sg.gearId === gear.id)) return false;
    return getExchangeCategoryCount(gear.categoryId) + 1 > gear.categoryMaxProducts;
  };

  const wouldExceedExceptionCategoryLimit = (gear: RiderProductModel): boolean => {
    if (!gear.categoryId || !gear.categoryMaxProducts) return false;
    if (exceptions.some(ex => ex.gearId === gear.id)) return false;
    return wouldExceedCategoryLimit(gear.categoryId, gear.categoryMaxProducts, 1);
  };

  const getRemainingInGearCategory = (gear: RiderProductModel): number | null => {
    return getRemainingInCategory(gear.categoryId, gear.categoryMaxProducts);
  };

  const getRemainingInExchangeCategory = (gear: RiderProductModel): number | null => {
    if (!gear.categoryId || !gear.categoryMaxProducts) return null;
    return gear.categoryMaxProducts - getExchangeCategoryCount(gear.categoryId);
  };

  const wouldExceedCategoryLimit = (categoryId?: number | null, max?: number | null, addQty: number = 1) => {
    if (!categoryId || !max) return false;

    const categoryCount = getAllSelectedCountByCategory();
    const current = categoryCount.get(categoryId) || 0;

    return current + addQty > max;
  };

  const getRemainingInCategory = (categoryId?: number | null, max?: number | null) => {
    if (!categoryId || !max) return null;

    const categoryCount = getAllSelectedCountByCategory();
    const current = categoryCount.get(categoryId) || 0;

    return max - current;
  };


  const openBoxKeyModal = (gearId: number) => {
    setCurrentBoxGearId(gearId);
    setTempBoxKey(boxKeys[gearId] || "");
    setShowBoxKeyModal(true);
  };

  const getOutgoingProductIdForGear = (gear: RiderProductModel): number => {
    const selected = selectedGears.find((x) => x.gearId === gear.id);
    if (selected?.groupSelectedProductId) return selected.groupSelectedProductId;
    if (selected?.replacementProductId) return selected.replacementProductId;
    return gear.productId;
  };

  const validateTempBoxKey = async (gearId: number) => {
    const value = tempBoxKey?.trim();
    if (!value) {
      setBoxKeyErrors((prev) => ({ ...prev, [gearId]: t("Key is required") }));
      return false;
    }
    const gear = gears.find((g) => g.id === gearId);
    if (!gear) return false;
    const productId = getOutgoingProductIdForGear(gear);
    try {
      const data = await stockService.validateBoxKey(productId, value);
      if (!data.valid) {
        setBoxKeyErrors((prev) => ({ ...prev, [gearId]: t("Invalid key") }));
        return false;
      }
      setBoxKeyErrors((prev) => ({ ...prev, [gearId]: null }));
      return true;
    } catch {
      setBoxKeyErrors((prev) => ({ ...prev, [gearId]: t("Validation failed") }));
      return false;
    }
  };

  const saveBoxKey = async () => {
    if (currentBoxGearId == null) return;
    const valid = await validateTempBoxKey(currentBoxGearId);
    if (!valid) return;
    setBoxKeys((prev) => ({ ...prev, [currentBoxGearId]: tempBoxKey.trim() }));
    setShowBoxKeyModal(false);
  };

  const validateTempMissingBoxKey = async (kitProductId: number) => {
    const value = tempMissingBoxKey.trim();
    if (!value) {
      setMissingBoxKeyErrors((p) => ({ ...p, [kitProductId]: t("Key is required") }));
      return false;
    }
    const kp = missingProducts.find((x) => x.id === kitProductId);
    if (!kp) return false;
    try {
      const data = await stockService.validateBoxKey(kp.productId, value);
      if (!data.valid) {
        setMissingBoxKeyErrors((p) => ({ ...p, [kitProductId]: t("Invalid key") }));
        return false;
      }
      setMissingBoxKeyErrors((p) => ({ ...p, [kitProductId]: null }));
      return true;
    } catch {
      setMissingBoxKeyErrors((p) => ({ ...p, [kitProductId]: t("Validation failed") }));
      return false;
    }
  };

  const saveMissingBoxKey = async () => {
    if (currentMissingKitProductId == null) return;
    const ok = await validateTempMissingBoxKey(currentMissingKitProductId);
    if (!ok) return;
    setMissingBoxKeys((p) => ({
      ...p,
      [currentMissingKitProductId]: tempMissingBoxKey.trim(),
    }));
    setShowMissingBoxKeyModal(false);
  };

  const openMissingBoxKeyModal = (kitProductId: number) => {
    setCurrentMissingKitProductId(kitProductId);
    setTempMissingBoxKey(missingBoxKeys[kitProductId] || "");
    setShowMissingBoxKeyModal(true);
  };

  const requiresBoxKey = (gear: RiderProductModel) => !!gear.boxId;

  const missingRequiredBoxKey = selectedGears.some((sg) => {
    const gear = gears.find((g) => g.id === sg.gearId);
    if (!gear) return false;
    if (!requiresBoxKey(gear)) return false;
    return !boxKeys[gear.id];
  });

  const missingRequiredBoxKeyForExceptions = exceptions.some((ex) => {
    const gear = gears.find((g) => g.id === ex.gearId);
    if (!gear) return false;
    if (!requiresBoxKey(gear)) return false;
    return !boxKeys[gear.id];
  });

  const missingRequiredBoxKeyForMissingProducts = missingProducts.some(
      (x) => x.checked && x.box && !missingBoxKeys[x.id]
  );

  const missingAnyRequiredBoxKey =
      missingRequiredBoxKey ||
      missingRequiredBoxKeyForExceptions ||
      missingRequiredBoxKeyForMissingProducts;

  useEffect(() => {
    let currnetSubTitle = subTitle;
    setSubTitle("");
    fetchStocks();
    fetchMaxTshirts();
    fetchTshirts();
    return () => {
      setSubTitle(currnetSubTitle);
    };
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (missingKitProducts?.products) {
      setMissingProducts(missingKitProducts.products);
    }
  }, [missingKitProducts?.products]);

  const fetchStocks = () => {
    startLoading("get-stock-list");
    stockService
        .listStocks()
        .then((stocks) => {
          setStocks(stocks);
        })
        .finally(() => {
          finnishLoading("get-stock-list");
        });
  };

  const fetchMaxTshirts = () => {
    riderService.getAssignTshirtsCount(rider.riderId).then((response) => {
      setMaxTshirtsCount(response);
    });
  };

  const fetchTshirts = () => {
    startLoading("exchange-fetch-tshirts");
    riderService
        .listTshirts(rider.riderId)
        .then((products) => {
          setTShirts(
              products.map((item) => {
                item.selectedVariationId = item.variations[0].id;
                return item;
              })
          );
        })
        .finally(() => {
          finnishLoading("exchange-fetch-tshirts");
        });
  };

  const enrichedGears = selectedGears.map((item) => ({
    ...item,
    replacementProductId: item.replacementProductId ?? null,
    boxKey: boxKeys[item.gearId] ?? null,
  }));

  const stockAvailable = (
      gear: RiderProductModel,
      exchangeProductId?: number | null
  ) => {
    let productStocks: StockModel[] = [];
    if (
        exchangeProductId &&
        gear.groupProducts?.find((x) => x.id === exchangeProductId)
    ) {
      productStocks = stocks.filter((x) => x.productId === exchangeProductId);
      let groupProduct = gear.groupProducts.find(
          (x) => x.id === exchangeProductId
      );
      if ((groupProduct?.variations.length ?? 0) > 0) {
        let stockAvailable = false;
        groupProduct?.variations.forEach((variation) => {
          if (
              (productStocks.find((x) => x.variationId === variation.id)
                  ?.quantity ?? -1) > 0
          ) {
            stockAvailable = true;
          }
        });
        return stockAvailable;
      } else if (productStocks.length === 1) {
        return productStocks[0].quantity > 0;
      }
    }
    if (gear.replacementProduct && gear.replacementProduct.length > 0) {
      let stockAvailable = false;
      gear.replacementProduct.forEach((product) => {
        const productStocks = stocks.filter((x) => x.productId === product.id);
        if (product.variations.length > 0) {
          product.variations.forEach((variation) => {
            const stock = productStocks.find(
                (x) => x.productId === product.id && x.variationId === variation.id
            );
            if ((stock?.quantity ?? 0) > 0) {
              stockAvailable = true;
            }
          });
        } else {
          const stock = productStocks.find(
              (x) => x.productId === product.id && x.variationId === null
          );
          if ((stock?.quantity ?? 0) > 0) {
            stockAvailable = true;
          }
        }
      });
      return stockAvailable;
    }
    productStocks = stocks.filter((x) => x.productId === gear.productId);
    if (gear.availableVariations.length > 0) {
      let stockAvailable = false;
      gear.availableVariations.forEach((variation) => {
        if (
            (productStocks.find((x) => x.variationId === variation.id)
                ?.quantity ?? -1) > 0
        ) {
          stockAvailable = true;
        }
      });
      return stockAvailable;
    }
    return (
        (productStocks.find((x) => x.variationId === null)?.quantity ?? -1) > 0
    );
  };

  const getFirstMissingBoxKeyGearId = (): number | null => {
    const sg = selectedGears.find((x) => {
      const g = gears.find((gg) => gg.id === x.gearId);
      return g && requiresBoxKey(g) && !boxKeys[g.id];
    });
    if (sg) return sg.gearId;
    const ex = exceptions.find((x) => {
      const g = gears.find((gg) => gg.id === x.gearId);
      return g && requiresBoxKey(g) && !boxKeys[g.id];
    });
    if (ex) return ex.gearId;
    return null;
  };

  const hasNonMissingExchangeSelection = () =>
      selectedGears.some((x) =>
          gears
              .filter((y) => y.exchangeList !== "size_change")
              .some((y) => y.id === x.gearId)
      ) ||
      returnedTshirts.length > 0 ||
      dispatchedTshirts.length > 0 ||
      exceptions.length > 0;

  const exchangeMultiple = (e: React.FormEvent) => {
    e.preventDefault();
    if (
        selectedGears.length < 1 &&
        returnedTshirts.length < 1 &&
        dispatchedTshirts.length < 1 &&
        missingProducts.filter((x) => x.checked).length < 1 &&
        exceptions.length < 1
    ) {
      return;
    }
    if (
        (selectedGears.some((x) =>
                gears
                    .filter((y) => y.exchangeList !== "size_change")
                    .some((y) => y.id === x.gearId)
            ) ||
            returnedTshirts.length > 0 ||
            dispatchedTshirts.length > 0 ||
            missingProducts.filter((x) => x.checked).length > 0 ||
            exceptions.length > 0) &&
        images.length < 1
    ) {
      return;
    }
    if (hasNonMissingExchangeSelection() && returnedItemsImages.length < 1) {
      return;
    }
    const missingReplacementException = exceptions.some((ex) => {
      const gear = gears.find((g) => g.id === ex.gearId);
      return (
          Array.isArray(gear?.replacementProduct) &&
          gear!.replacementProduct.length > 0 &&
          ex.replacementProductId == null
      );
    });
    if (missingReplacementException) {
      showInfo(t("Please select a replacement product for all exception products!"));
      return;
    }
    if (
        selectedGears.some((x) => x.variationId === -1) ||
        exceptions.some((x) => x.variationId === -1)
    ) {
      showInfo(t("Please select a size for all exchange products!"));
      return;
    }
    const outOfStockException = exceptions.find((ex) => {
      const gear = gears.find((g) => g.id === ex.gearId);
      if (!gear) return false;
      const productId = ex.replacementProductId ?? gear.productId;
      const variationId =
          ex.variationId != null && ex.variationId !== -1 ? ex.variationId : null;
      return (
          (stocks.find(
              (s) => s.productId === productId && s.variationId === variationId
          )?.quantity ?? 0) < 1
      );
    });
    if (outOfStockException) {
      showInfo(t("Some exception products are out of stock!"));
      return;
    }
    const missingGearId = getFirstMissingBoxKeyGearId();
    if (missingGearId != null) {
      openBoxKeyModal(missingGearId);
      setBoxKeyErrors((prev) => ({ ...prev, [missingGearId]: t("Key is required") }));
      return;
    }
    const firstMissingNeedingBoxKey = missingProducts.find(
        (x) => x.checked && x.box && !missingBoxKeys[x.id]
    );
    if (firstMissingNeedingBoxKey) {
      openMissingBoxKeyModal(firstMissingNeedingBoxKey.id);
      setMissingBoxKeyErrors((p) => ({
        ...p,
        [firstMissingNeedingBoxKey.id]: t("Key is required"),
      }));
      return;
    }
    const enrichedMissingProducts = missingProducts
        .filter((x) => x.checked)
        .map((x) => ({
          ...x,
          boxKey: x.box ? (missingBoxKeys[x.id] ?? null) : null,
        }));
    startLoading("profile-multiple-exchange");
    riderService
        .multiExchange(
            rider.riderId,
            enrichedGears,
            returnedTshirts,
            dispatchedTshirts,
            enrichedMissingProducts,
            exceptions,
            images[0],
            returnedItemsImages[0]
        )
        .then(() => {
          setSelectedGears([]);
          setReturnedTshirts([]);
          setDispatchedTshirts([]);
          setImages([]);
          setReturnedItemsImages([]);
          updateGears();
          fetchStocks();
          setMissingProducts([]);
          fetchMissingKitProducts();
          setExceptions([]);
          setMaxTshirtsCount({ maxTshirts: 0, returnModifier: 0 });
          fetchMaxTshirts();
          showInfo(t("Items successfully exchanged!"));
        })
        .catch((reason) => {
          console.log(reason);
          if (reason.message === "This request has already been processed.") {
            showInfo(t("This request has already been processed."));
          } else {
            showInfo(t("Items exchange was unsuccessfull!"));
          }
        })
        .finally(() => {
          finnishLoading("profile-multiple-exchange");
        });
  };

  useEffect(() => {
    const newRequestId = uuidv4();
    localStorage.setItem('multiExchangeRequestId', newRequestId);
    console.log('newRequestId', newRequestId);
  }, []);

  const getExchangeListItem = (gear: RiderProductModel) => {
    const selectedGear = selectedGears.find((x) => x.gearId === gear.id);
    const selectedReplacement = gear.replacementProduct?.find(
        (p) => p.id === selectedGear?.replacementProductId
    );

    const handleReplacementChange = (productId: number) => {
      const replacement = gear.replacementProduct?.find((p) => p.id === productId);
      let firstAvailableVariationId: number | null = null;
      if (replacement?.variations?.length) {
        const firstAvailable = replacement.variations.find((v) =>
            isInStock(replacement.id, v.id)
        );
        firstAvailableVariationId = firstAvailable?.id ?? -1;
      }
      setSelectedGears((prev) => [
        ...prev.filter((x) => x.gearId !== gear.id),
        {
          gearId: gear.id,
          replacementProductId: productId,
          variationId: firstAvailableVariationId,
        },
      ]);
    };

    const handleVariationChange = (variationId: number) => {
      setSelectedGears((prev) => {
        const existing = prev.find((item) => item.gearId === gear.id);
        if (existing) {
          return prev.map((item) =>
              item.gearId === gear.id ? { ...item, variationId } : item
          );
        } else {
          return [
            ...prev,
            {
              gearId: gear.id,
              variationId,
              replacementProductId: hasReplacement ? gear.replacementProduct?.[0]?.id ?? null : null,
            },
          ];
        }
      });
    };

    const resetReplacement = () => {
      setSelectedGears((prev) => prev.filter((x) => x.gearId !== gear.id));
    };

    const isInStock = (productId: number, variationId: number | null) => {
      const stock = stocks.find(
          (x) => x.productId === productId && x.variationId === variationId
      );
      return (stock?.quantity ?? 0) > 0;
    };

    const hasReplacement =
        Array.isArray(gear.replacementProduct) &&
        gear.replacementProduct.length > 0;
    const isReplacementSelected = selectedReplacement !== undefined;
    const availableReplacementOptions = Array.isArray(gear.replacementProduct)
        ? gear.replacementProduct.filter((p) =>
            p.variations.length > 0
                ? p.variations.some((v) => isInStock(p.id, v.id))
                : isInStock(p.id, null)
        )
        : [];
    const hasNoReplacementOptions = hasReplacement && availableReplacementOptions.length === 0;
    const isOwnOutOfStock =
        (!hasReplacement && !stockAvailable(gear)) || hasNoReplacementOptions;
    const isSelected = !!selectedGears.find((x) => x.gearId === gear.id);

    return (
        <div
            className={`product-list-card mb-4 ${selectedGear ? "marked" : ""} ${wouldExceedExchangeCategoryLimit(gear) ? "opacity-50" : ""}`}
            key={gear.id}
        >
          <div className="relative">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="product-list-card__image">
                  <img
                      src={
                        isReplacementSelected
                            ? selectedReplacement?.image ?? "https://via.placeholder.com/60"
                            : gear.productImage ?? "https://via.placeholder.com/60"
                      }
                      alt="product"
                  />
                </div>
              </div>
              <div className="w-full">
                <div className="product-list-card__title">
                  {isReplacementSelected
                      ? selectedReplacement?.name
                      : gear.productName}
                  {/* TODO: Remove this debug info after testing category limits */}
                  <div className="text-xs text-gray-500 mt-1">
                    Category: {gear.categoryName ?? 'N/A'} |
                    Max: {gear.categoryMaxProducts ? gear.categoryMaxProducts : 'No limit'}
                  </div>

                  {/* Remaining warning */}
                  {(() => {
                    const remaining = getRemainingInExchangeCategory(gear);
                    return remaining !== null && remaining === 1 && !isSelected && (
                        <div className="text-xs text-orange-500 mt-1">
                          {t('Only {{count}} items remaining in this category', { count: remaining })}
                        </div>
                    );
                  })()}

                  {/* Category limit reached */}
                  {wouldExceedExchangeCategoryLimit(gear) && (
                      <div className="text-xs text-red-500 mt-1">
                        {t('Category limit reached')}
                      </div>
                  )}

                  <br/>
                  {gear.boxId && (
                      <span className="font-12 color-secondary">
                        &nbsp;({gear.boxId})
                      </span>
                  )}
                  {requiresBoxKey(gear) && isSelected && (
                      <>
                        {boxKeys[gear.id] && (
                            <div className="font-12 color-secondary">
                              {t("Reference")}: {boxKeys[gear.id]}
                            </div>
                        )}
                        <button
                            type="button"
                            className="button button--small button--light"
                            onClick={() => openBoxKeyModal(gear.id)}
                        >
                          {boxKeys[gear.id] ? t("Edit reference") : t("Set reference")}
                        </button>
                      </>
                  )}
                </div>
                {((!hasReplacement && !stockAvailable(gear)) || hasNoReplacementOptions) && (
                    <span className="text-red">{t("Out of stock")}</span>
                )}
                {hasReplacement && !isReplacementSelected && !hasNoReplacementOptions && (
                    <select
                        className={`input-field input-field--small input-field--auto-width ${!isReplacementSelected ? "text-gray" : "text-blue"}`}
                        value={-1}
                        onChange={(e) => handleReplacementChange(parseInt(e.target.value))}
                        disabled={wouldExceedExchangeCategoryLimit(gear)}
                    >
                      <option value={-1}>- Please Select -</option>
                      {availableReplacementOptions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                      ))}
                    </select>
                )}
                {hasReplacement && isReplacementSelected && (
                    <span className="text-blue" onClick={resetReplacement}>
                      {t("<< Select an other product")}
                    </span>
                )}
              </div>
              <div className="flex gap-2 items-center">
                {isReplacementSelected && selectedReplacement?.variations.length > 0 && (
                    <select
                        className="input-field input-field--small input-field--variation-width"
                        value={selectedGear?.variationId ?? -1}
                        onChange={(e) => handleVariationChange(parseInt(e.target.value))}
                        disabled={wouldExceedExchangeCategoryLimit(gear)}
                    >
                      <option value={-1}>{t("Select size")}</option>
                      {selectedReplacement.variations
                          .filter((v) => isInStock(selectedReplacement.id, v.id))
                          .map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.name}
                              </option>
                          ))}
                    </select>
                )}
                {!hasReplacement && gear.availableVariations.length > 0 && !isOwnOutOfStock && (
                    <select
                        className="input-field input-field--small input-field--variation-width"
                        value={selectedGear?.variationId ?? -1}
                        onChange={(e) => handleVariationChange(parseInt(e.target.value))}
                        disabled={wouldExceedExchangeCategoryLimit(gear)}
                    >
                      <option value={-1}>{t("Select size")}</option>
                      {gear.availableVariations
                          .filter((v) => isInStock(gear.productId, v.id))
                          .map((v) => (
                              <option key={v.id} value={v.id}>
                                {v.name}
                              </option>
                          ))}
                    </select>
                )}
                {!isOwnOutOfStock && (
                    <button
                        type="button"
                        disabled={wouldExceedExchangeCategoryLimit(gear)}
                        className={`${
                            selectedGear
                                ? "product-list-card__check checked"
                                : "product-list-card__check needtocheck"
                        } ${wouldExceedExchangeCategoryLimit(gear) ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={() => {
                          if (!selectedGear && wouldExceedExchangeCategoryLimit(gear)) {
                            showInfo(t('Maximum {{max}} products allowed from this category', {
                              max: gear.categoryMaxProducts
                            }));
                            return;
                          }
                          if (selectedGear) {
                            setSelectedGears((prev) => prev.filter((x) => x.gearId !== gear.id));
                          } else {
                            const existingGear = selectedGears.find((x) => x.gearId === gear.id);
                            const defaultVariationId =
                                existingGear?.variationId !== undefined
                                    ? existingGear.variationId
                                    : hasReplacement && selectedReplacement?.variations.length
                                        ? selectedReplacement.variations.find((v) =>
                                        isInStock(selectedReplacement.id, v.id)
                                    )?.id ?? -1
                                        : gear.availableVariations.length > 0
                                            ? gear.availableVariations.find((v) =>
                                            isInStock(gear.productId, v.id)
                                        )?.id ?? -1
                                            : null;
                            setSelectedGears((prev) => [
                              ...prev.filter((x) => x.gearId !== gear.id),
                              {
                                gearId: gear.id,
                                variationId: defaultVariationId,
                                replacementProductId:
                                    gear.exchangeList === "size_change"
                                        ? gear.productId
                                        : isReplacementSelected
                                            ? selectedReplacement?.id ?? null
                                            : null,
                              },
                            ]);
                          }
                        }}
                    >
                      {selectedGear && <CheckIcon />}
                    </button>
                )}
              </div>
              <div className="exchange-list-card__exchange-type-icon-holder">
                {gear.exchangeList === "size_change" ? (
                    <Popover defaultPosition={"left"} text={"Size change"}>
                      <div className="exchange-list-card__exchange-type-icon">
                        <ExchangeSizeIcon/>
                      </div>
                    </Popover>
                ) : gear.exchangeList === "accident" ? (
                    <Popover defaultPosition={"left"} text={t('Accident')}>
                      <div className="exchange-list-card__exchange-type-icon">
                        <ExchangeAccidentIcon/>
                      </div>
                    </Popover>
                ) : (
                    <Popover defaultPosition={"left"} text={"Eligible exchange"}>
                      <div className="exchange-list-card__exchange-type-icon">
                        <ExchangeLifecycleIcon/>
                      </div>
                    </Popover>
                )}
              </div>
            </div>
          </div>
        </div>
    );
  };

  const addExceptionData = (
      condition: ReturnCondition,
      image: File,
      note: string | null
  ) => {
    if (!selectedExceptionGearId) return;
    const existing = exceptions.find((x) => x.gearId === selectedExceptionGearId);
    setExceptions((prev) => [
      ...prev.filter((x) => x.gearId !== selectedExceptionGearId),
      {
        gearId: selectedExceptionGearId,
        variationId:
            (gears.find((x) => x.id === selectedExceptionGearId)?.availableVariations?.length ?? 0) > 0
                ? -1
                : null,
        replacementProductId: existing?.replacementProductId ?? null,
        condition,
        image,
        note,
      },
    ]);
    setSelectedExceptionGearId(null);
  };

  if (stocks.length === 0) {
    return null;
  }

  if (selectedExceptionGearId) {
    const selectedExceptionGear = gears.find((gear) => gear.id === selectedExceptionGearId);
    return (
        <div
            className={"modal" + (!selectedExceptionGearId ? " hidden" : "")}
            tabIndex={-1}
            aria-modal="true"
            role="dialog"
        >
          <div className="modal__dialog modal-dialog--full-height">
            <div className="modal__body">
              <div className="navigationbar navigationbar--dark mb-6">
                <div className="navigationbar__left">
                  <span
                      onClick={() => {
                        setExceptions((prev) => prev.filter((x) => x.gearId !== selectedExceptionGearId));
                        setSelectedExceptionGearId(null);
                      }}
                      className="navigationbar__button">
                    <LongArrowLeftIcon />
                  </span>
                </div>
                <div className="navigationbar__center">
                  <div className="navigationbar__title">{t("Exception")}</div>
                </div>
                <div className="navigationbar__right">
                </div>
              </div>
              <div className="product-list-card mb-6" key={selectedExceptionGear!.id}>
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <div className="product-list-card__image">
                        <img src={selectedExceptionGear!.productImage} alt="package name"/>
                      </div>
                      {selectedExceptionGear!.availableVariations.length > 0 && (
                          <div className="product-list-card__variation-name">
                            {selectedExceptionGear!.availableVariations.find((v) => v.id === selectedExceptionGear!.productVariationId)?.name || "N/A"}
                          </div>
                      )}
                    </div>
                    <div className="w-full">
                      <div className="product-list-card__title">
                        {selectedExceptionGear!.productName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="heading mb-3">{t("Type of the problem")}</div>
              <ProductState
                  saveButtonOnClick={addExceptionData}
                  backButtonOnClick={() => {
                    setExceptions((prev) => prev.filter((x) => x.gearId !== selectedExceptionGearId));
                    setSelectedExceptionGearId(null);
                  }}
                  initialData={
                      exceptions.find((x) => x.gearId === selectedExceptionGearId) || {
                        condition: null,
                        image: null,
                        note: null,
                      }
                  }
              />
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="">
        <form onSubmit={exchangeMultiple}>
          {gears
              .filter((x) => x.exchangeList === "eligible" && !x.isTshirt)
              .map((gear) => {
                return getExchangeListItem(gear);
              })}
          <ExchangeTshirts
              dispatchedTshirts={dispatchedTshirts}
              returnedTshirts={returnedTshirts}
              gears={gears}
              setDispatchedTshirts={setDispatchedTshirts}
              setReturnedTshirts={setReturnedTshirts}
              stocks={stocks}
              maxTshirtsCount={maxTshirtsCount}
              tShirts={tShirts}
              setTShirts={setTShirts}
          />
          {gears.some((x) => x.exchangeList === "accident") ? (
              <h1 className="heading mb-2">
                {/* {t("Accidents")} */}
              </h1>
          ) : null}
          {gears
              .filter((x) => x.exchangeList === "accident")
              .map((gear) => {
                return getExchangeListItem(gear);
              })}
          {missingProducts.length > 0 ? (
              <ExchangeMissing
                  missingProducts={missingProducts}
                  setMissingProducts={setMissingProducts}
                  stocks={stocks}
                  boxKeys={missingBoxKeys}
                  openBoxKeyModal={openMissingBoxKeyModal}
                  selectedGears={selectedGears}
                  gears={gears}
                  wouldExceedCategoryLimit={wouldExceedCategoryLimit}
                  getRemainingInCategory={getRemainingInCategory}
              />
          ) : null}
          {gears
              .filter((x) => x.exchangeList === "size_change")
              .map((gear) => {
                return getExchangeListItem(gear);
              })}
          {gears
              .filter((x) => x.exchangeList === "exception")
              .map((gear) => {
                const exception = exceptions.find((x) => x.gearId === gear.id);
                const selectedReplacement = gear.replacementProduct?.find(
                    (p) => p.id === exception?.replacementProductId
                );
                const isInStock = (productId: number, variationId: number | null) => {
                  return (
                      (stocks.find(
                          (s) => s.productId === productId && s.variationId === variationId
                      )?.quantity ?? 0) > 0
                  );
                };
                const hasReplacement = Array.isArray(gear.replacementProduct) && gear.replacementProduct.length > 0;
                const isReplacementSelected = !!selectedReplacement;
                const exceedLimit = wouldExceedExceptionCategoryLimit(gear);
                const remainingInCat = getRemainingInGearCategory(gear);
                const isOutOfStock = !stockAvailable(
                    gear,
                    exception?.replacementProductId
                );
                return (
                    <div
                        className={`product-list-card mb-4 ${
                            exception ? "marked" : ""
                        } ${(!exception && exceedLimit) || (!exception && isOutOfStock) ? "opacity-50" : ""}`}
                        key={gear.id}
                    >
                      <div className="relative">
                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <div className="product-list-card__image">
                              <img
                                  src={
                                      selectedReplacement?.image ??
                                      gear.productImage ??
                                      "https://via.placeholder.com/60"
                                  }
                                  alt="product"
                              />
                            </div>
                          </div>
                          <div className="w-full">
                            <div className="product-list-card__title">
                              {isReplacementSelected
                                  ? selectedReplacement?.name
                                  : gear.productName}

                              {/* DEBUG */}
                              <div className="text-xs text-gray-500 mt-1">
                                Category: {gear.categoryName ?? 'N/A'} | Max: {gear.categoryMaxProducts ? gear.categoryMaxProducts : 'No limit'}
                              </div>

                              {/* Remaining warning */}
                              {remainingInCat !== null && remainingInCat === 1 && !exception && (
                                  <div className="text-xs text-orange-500 mt-1">
                                    {t('Only {{count}} items remaining in this category', { count: remainingInCat })}
                                  </div>
                              )}

                              {/* Category limit reached */}
                              {exceedLimit && !exception && (
                                  <div className="text-xs text-red-500 mt-1">
                                    {t('Category limit reached')}
                                  </div>
                              )}
                            </div>
                            {isOutOfStock && (
                                <span className="text-red">{t("Out of stock")}</span>
                            )}
                            <div className="exchange-list-card__exchange-type-icon-holder">
                              <Popover defaultPosition={"left"} text={"Exception"}>
                                <div className="exchange-list-card__exchange-type-icon">
                                  <ExchangeExceptionIcon />
                                </div>
                              </Popover>
                            </div>
                            {hasReplacement && !isReplacementSelected && (
                                <select
                                    className="input-field input-field--small input-field--auto-width"
                                    value={-1}
                                    onChange={(e) => {
                                      const productId = parseInt(e.target.value);
                                      const product = gear.replacementProduct?.find(
                                          (p) => p.id === productId
                                      );
                                      let variationId: number | null = null;
                                      if (product?.variations?.length) {
                                        const firstAvailable = product.variations.find((v) =>
                                            isInStock(product.id, v.id)
                                        );
                                        variationId = firstAvailable?.id ?? -1;
                                      }
                                      setExceptions((prev) =>
                                          prev.map((item) =>
                                              item.gearId === gear.id
                                                  ? {
                                                    ...item,
                                                    replacementProductId: productId,
                                                    variationId,
                                                  }
                                                  : item
                                          )
                                      );
                                    }}
                                    disabled={!exception || exceedLimit}
                                >
                                  <option value={-1}>- {t("Select replacement product")} -</option>
                                  {Array.isArray(gear.replacementProduct) &&
                                      gear.replacementProduct
                                          .filter((p) =>
                                              p.variations.length > 0
                                                  ? p.variations.some((v) =>
                                                      isInStock(p.id, v.id)
                                                  )
                                                  : isInStock(p.id, null)
                                          )
                                          .map((p) => (
                                              <option key={p.id} value={p.id}>
                                                {p.name}
                                              </option>
                                          ))}
                                </select>
                            )}
                            {hasReplacement && isReplacementSelected && (
                                <span
                                    className="text-blue cursor-pointer"
                                    onClick={() => {
                                      setExceptions((prev) =>
                                          prev.map((item) =>
                                              item.gearId === gear.id
                                                  ? {
                                                    ...item,
                                                    replacementProductId: null,
                                                    variationId: -1,
                                                  }
                                                  : item
                                          )
                                      );
                                    }}
                                >
                                  {t("<< Select an other product")}
                                </span>
                            )}
                          </div>
                          <div className="flex gap-2 items-center">
                            {isReplacementSelected &&
                                selectedReplacement?.variations.length > 0 && (
                                    <select
                                        className="input-field input-field--small input-field--variation-width"
                                        onChange={(e) => {
                                          const newVarId = parseInt(e.target.value);
                                          setExceptions((prev) =>
                                              prev.map((item) =>
                                                  item.gearId === gear.id
                                                      ? { ...item, variationId: newVarId }
                                                      : item
                                              )
                                          );
                                        }}
                                        value={exception?.variationId ?? -1}
                                        disabled={!exception || exceedLimit}
                                    >
                                      <option value={-1}>{t("Select size")}</option>
                                      {selectedReplacement.variations
                                          .filter((v) =>
                                              isInStock(selectedReplacement.id, v.id)
                                          )
                                          .map((v) => (
                                              <option key={v.id} value={v.id}>
                                                {v.name}
                                              </option>
                                          ))}
                                    </select>
                                )}
                            {!hasReplacement &&
                                gear.availableVariations.length > 0 && (
                                    <select
                                        className="input-field input-field--small input-field--variation-width"
                                        onChange={(e) => {
                                          const varId = parseInt(e.target.value);
                                          setExceptions((prev) =>
                                              prev.map((item) =>
                                                  item.gearId === gear.id
                                                      ? { ...item, variationId: varId }
                                                      : item
                                              )
                                          );
                                        }}
                                        value={exception?.variationId ?? -1}
                                        disabled={!exception || exceedLimit}
                                    >
                                      <option value={-1}>{t("Select size")}</option>
                                      {gear.availableVariations
                                          .filter((v) => isInStock(gear.productId, v.id))
                                          .map((v) => (
                                              <option key={v.id} value={v.id}>
                                                {v.name}
                                              </option>
                                          ))}
                                    </select>
                                )}
                            {(!isOutOfStock || !!exception) && (
                            <div
                                className={`icon-button icon-button--square icon-button--purple ${
                                    !exception && exceedLimit ? "opacity-50 cursor-not-allowed" : ""
                                }`}
                                onClick={() => {
                                  if (!gear.exchangedDisabled && !gear.discontinued) {
                                    if (!exception && wouldExceedExceptionCategoryLimit(gear)) {
                                      showInfo(t('Maximum {{max}} products allowed from this category', {
                                        max: gear.categoryMaxProducts
                                      }));
                                      return;
                                    }
                                    setSelectedExceptionGearId(gear.id);
                                  }
                                }}
                            >
                              <div
                                  className={
                                      "status " + (exception ? "closed" : "open")
                                  }
                              >
                                {exception ? <CheckIcon /> : <ExchangeLifecycleIcon />}
                              </div>
                            </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                );
              })}
          {showBoxKeyModal && (
              <div className="info-modal modal" tabIndex={-1} role="dialog">
                <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
                  <h3 className="text-lg font-semibold mb-4 text-center">
                    {t("Set Reference")}
                  </h3>
                  <input
                      className="input input--text input-field w-full"
                      value={tempBoxKey}
                      onChange={(e) => setTempBoxKey(e.target.value)}
                      onBlur={() => currentBoxGearId != null && validateTempBoxKey(currentBoxGearId)}
                      placeholder="Enter reference"
                  />
                  {currentBoxGearId != null && boxKeyErrors[currentBoxGearId] && (
                      <div className="text-red-500 text-sm mt-1">
                        {boxKeyErrors[currentBoxGearId]}
                      </div>
                  )}
                  <div className="flex justify-end gap-3 mt-6">
                    <button type="button" className="button" onClick={saveBoxKey}>
                      {t("Save")}
                    </button>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        className="button button--light"
                        onClick={() => setShowBoxKeyModal(false)}
                    >
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              </div>
          )}
          {showMissingBoxKeyModal && (
              <div className="info-modal modal" tabIndex={-1} role="dialog">
                <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
                  <h3 className="text-lg font-semibold mb-4 text-center">
                    {t("Set Reference")}
                  </h3>
                  <input
                      className="input input--text input-field w-full"
                      value={tempMissingBoxKey}
                      onChange={(e) => setTempMissingBoxKey(e.target.value)}
                      onBlur={() =>
                          currentMissingKitProductId != null &&
                          validateTempMissingBoxKey(currentMissingKitProductId)
                      }
                      placeholder="Enter reference"
                  />
                  {currentMissingKitProductId != null && missingBoxKeyErrors[currentMissingKitProductId] && (
                      <div className="text-red-500 text-sm mt-1">
                        {missingBoxKeyErrors[currentMissingKitProductId]}
                      </div>
                  )}
                  <div className="flex justify-end gap-3 mt-6">
                    <button type="button" className="button" onClick={saveMissingBoxKey}>
                      {t("Save")}
                    </button>
                  </div>
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                        type="button"
                        className="button button--light"
                        onClick={() => setShowMissingBoxKeyModal(false)}
                    >
                      {t("Cancel")}
                    </button>
                  </div>
                </div>
              </div>
          )}
          {selectedGears.length > 0 ||
          returnedTshirts.length > 0 ||
          dispatchedTshirts.length > 0 ||
          missingProducts.filter((x) => x.checked).length > 0 ||
          exceptions.length > 0 ? (
              <>
                {hasNonMissingExchangeSelection() && (
                    <div className="mt-4 mb-4">
                      <ImageInput
                          images={returnedItemsImages}
                          setImages={setReturnedItemsImages}
                          required={hasNonMissingExchangeSelection()}
                          infoText={t("Returned items photo")}
                      />
                    </div>
                )}
                <div className="mt-4 mb-4">
                  <ImageInput
                      images={images}
                      setImages={setImages}
                      required={
                          selectedGears.some((x) =>
                              gears
                                  .filter((y) => y.exchangeList !== "size_change")
                                  .some((y) => y.id === x.gearId)
                          ) ||
                          returnedTshirts.length > 0 ||
                          dispatchedTshirts.length > 0 ||
                          missingProducts.filter((x) => x.checked).length > 0 ||
                          exceptions.length > 0
                      }
                      infoText={t("Confirmation photo")}
                  />
                </div>
                <button
                    type="submit"
                    className={"button w-full " + (missingAnyRequiredBoxKey ? "button--disabled" : "")}
                    disabled={missingAnyRequiredBoxKey}
                >
                  {t("Exchange")} / {t("Add")}
                </button>
              </>
          ) : null}
        </form>
      </div>
  );
};

export default Exchange;